import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Table } from "semantic-ui-react";
import { useCultivationFormFieldData, useGetCultivationFormData } from "../../../../api/farmGate";
import { useGetCountriesData, useGetProduceType } from "../../../../api/static-data";
import { TitleView } from "../../../../components";
import ConfirmModal from "../../../../components/confirmViewModal/ConfirmModal";
import { InputNumber } from "../../../../components/InputText/InputNumber";
import { CULTIVATION_LABEL_TYPE } from "../../../../config/constants";
import "../mappingDataView.scss";
import {
  calculateTotalRows,
  cultivationData,
  getAllCultivationGroups,
  renderEmptyRow,
  renderGroupSubHeader
} from "./CultivationTableHelpers";
import { editDataRow, renderTableHeader } from "./CultivationTabletContent";

const CultivationTable = ({
  reviewData,
  fertilizerData,
  pesticideData,
  isEditable,
  refetch
}: any) => {
  const [selectedCultivation, setSelectedCultivation] = useState<any>(null);
  const [viewModal, setViewModal] = useState(false);
  const {
    mutate: cultivationFormFieldDataMutate,
    isLoading: isCultivationFormFieldDataMutateLoading
  } = useCultivationFormFieldData();
  const {
    data: currentFormData,
    status: formFetchStatus,
    fetchStatus: formFetchState,
    refetch: refetchFormData
  } = useGetCultivationFormData(selectedCultivation?.ownId);
  const { data: countriesData, isLoading: isCountriesDataLoading } = useGetCountriesData();
  const {
    data: produceTypes,
    isSuccess: isProduceTypesSuccess,
    isLoading: isProduceTypesLoading
  } = useGetProduceType();

  const values = useMemo(() => {
    return {
      fertilizerQuantity:
        currentFormData?.cultivationField?.fertilizerList?.find(
          (item: any) => parseInt(item.id) === parseInt(selectedCultivation?.id)
        )?.fertilizerQuantity || 0,
      pesticideQuantity:
        currentFormData?.cultivationField?.pesticideList?.find(
          (item: any) => parseInt(item.id) === parseInt(selectedCultivation?.id)
        )?.pesticideQuantity || 0
    };
  }, [selectedCultivation]);

  const {
    formState: { errors },
    control,
    setValue,
    handleSubmit,
    reset
  } = useForm({ defaultValues: values, values: values });

  const cultivationCurrentData = useMemo(() => {
    const currentCultivationDetails = cultivationData(
      reviewData?.farmGate || [],
      fertilizerData,
      pesticideData
    );
    return currentCultivationDetails;
  }, [reviewData, fertilizerData, pesticideData]);

  function saveFertilizerPesticideData(fertilizerList: any[], pesticideList: any[]) {
    cultivationFormFieldDataMutate(
      {
        farmGateId: selectedCultivation.ownId,
        fertilizerList: fertilizerList,
        pesticideList: pesticideList
      },
      {
        onSuccess() {
          refetch();
          refetchFormData();
          setSelectedCultivation(null);
        }
      }
    );
  }

  const deleteData = () => {
    let fertilizerList = currentFormData?.cultivationField?.fertilizerList || [];
    let pesticideList = currentFormData?.cultivationField?.pesticideList || [];

    if (selectedCultivation.fertilizer_name) {
      fertilizerList = fertilizerList.filter(
        (fertilizer: any) => fertilizer.id !== selectedCultivation.id
      );
    }

    if (selectedCultivation.pesticide_name) {
      pesticideList = pesticideList.filter(
        (pesticide: any) => pesticide.id !== selectedCultivation.id
      );
    }

    saveFertilizerPesticideData(fertilizerList, pesticideList);
    setViewModal(false);
  };

  const getItemQuantity = (item: any): any => {
    const countryName = countriesData?.find((c: any) => c.key === item?.country)?.text || "";
    const isEditing =
      item?.id === selectedCultivation?.id && item?.ownId === selectedCultivation?.ownId;
    const quantityFieldName =
      item?.label === CULTIVATION_LABEL_TYPE.Fertilizer
        ? "fertilizerQuantity"
        : item?.label === CULTIVATION_LABEL_TYPE.Pesticide
        ? "pesticideQuantity"
        : null;

    if (isEditing && quantityFieldName) {
      return (
        <InputNumber
          transform={{
            input: (value: any) =>
              value === null || value === undefined || isNaN(value) ? "" : value.toString(),
            output: (e: any) => e
          }}
          control={control}
          placeholder="kg/ha"
          name={quantityFieldName}
          errors={errors?.[quantityFieldName]}
          required
          errorMessage="Quantity is required"
        />
      );
    }
    switch (item?.label) {
      case CULTIVATION_LABEL_TYPE.GenericUser:
        return countryName ?? 0;
      case CULTIVATION_LABEL_TYPE.Fertilizer:
        return item?.fertilizerQuantity ?? 0;
      case CULTIVATION_LABEL_TYPE.Pesticide:
        return item?.pesticideQuantity ?? 0;
      default:
        return item?.value ?? 0;
    }
  };

  function renderDataRow({
    dataIndex,
    groupIndex,
    index,
    currentRow,
    totalRows,
    rowCount,
    packName,
    label,
    item,
    extraIndex
  }: any) {
    return (
      <Table.Row key={`${dataIndex}-${groupIndex}-${index}`}>
        <>
          {currentRow === 0 && <Table.Cell rowSpan={totalRows + extraIndex}>{packName}</Table.Cell>}
          {index === 0 && <Table.Cell rowSpan={rowCount}>{label}</Table.Cell>}
          <Table.Cell>
            {item?.fertilizer_name ||
              item?.pesticide_name ||
              item?.landUsageChange ||
              item?.produceType}
          </Table.Cell>
          <Table.Cell>{getItemQuantity({ label, ...item })}</Table.Cell>
          <Table.Cell>
            {label === CULTIVATION_LABEL_TYPE.GenericUser ? item?.harvestYear : ""}
          </Table.Cell>
          <Table.Cell>
            {label === CULTIVATION_LABEL_TYPE.GenericUser ? item?.growthArea : ""}
          </Table.Cell>
          <Table.Cell>
            {editDataRow(
              label,
              item,
              selectedCultivation,
              currentFormData,
              handleSubmit,
              saveFertilizerPesticideData,
              setSelectedCultivation,
              setValue,
              control,
              errors,
              isCultivationFormFieldDataMutateLoading,
              setViewModal,
              reset,
              isEditable
            )}
          </Table.Cell>
        </>
      </Table.Row>
    );
  }

  return (
    <>
      <TitleView CustomTitleViewMain="mt-2" title="Cultivation" />
      <Table celled className="table" structured>
        {renderTableHeader()}
        <Table.Body>
          {cultivationCurrentData.map((data: any, dataIndex: number) => {
            const packName = data?.cultivationName;
            const allCultivation = getAllCultivationGroups(data, produceTypes);
            const totalRows = calculateTotalRows(allCultivation);
            let currentRow = 0;
            const renderedRows: JSX.Element[] = [];

            const extraIndex = data?.pesticideListData?.length == 0 ? 1 : 2;

            for (let groupIndex = 0; groupIndex < allCultivation.length; groupIndex++) {
              const group = allCultivation[groupIndex];
              const { label, items } = group;
              const rowCount = items.length || 1;

              if (items.length === 0) {
                renderedRows.push(
                  renderEmptyRow(dataIndex, groupIndex, currentRow, totalRows, packName, label)
                );
                currentRow++;
                continue;
              }

              for (let index = 0; index < items.length; index++) {
                if (index === 0) {
                  const headerRow = renderGroupSubHeader(label);
                  if (headerRow) {
                    renderedRows.push(
                      <Table.Row
                        key={`${dataIndex}-${groupIndex}-sub-header`}
                        className="group-sub-header"
                      >
                        {headerRow.props.children}
                      </Table.Row>
                    );
                    currentRow++;
                  }
                }
                renderedRows.push(
                  renderDataRow({
                    dataIndex,
                    groupIndex,
                    index,
                    currentRow,
                    totalRows,
                    rowCount,
                    packName,
                    label,
                    item: items[index],
                    extraIndex
                  })
                );
                currentRow++;
              }
            }
            return renderedRows;
          })}
        </Table.Body>
      </Table>
      <ConfirmModal
        viewModal={viewModal}
        closeModal={() => setViewModal(false)}
        cancel={() => {
          setSelectedCultivation(null);
          setViewModal(false);
        }}
        approve={() => {
          deleteData();
        }}
        title="Delete Cultivation Data"
        subTitle="Are you sure you want to delete cultivation data?"
      />
    </>
  );
};

export default CultivationTable;
